import React from 'react';
import { Listbox } from '@headlessui/react';
import classNames from 'classnames';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { ColumnSelectItem } from 'lib/components/ColumnSelect/ColumnSelectItem';
import { Label } from 'lib/components/Label';

type Option = {
  value: string;
  label: string;
};

type MultiselectProps = {
  value: string[];
  onChange: (value: string[]) => void;
  options: Option[];
  placeholder?: string;
  labelText?: string;
};

/**
 * A select component that, unlike ColumnSelect, allows for multiple selections.
 */
export function Multiselect({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  labelText
}: MultiselectProps) {
  const selectedOptions = options.filter(option =>
    value.includes(option.value)
  );

  const getSelectLabelValue = () => {
    if (selectedOptions.length === 0) {
      return placeholder;
    }
    if (selectedOptions.length === 1) {
      return selectedOptions[0].label;
    }
    return `${selectedOptions.length} selected`;
  };

  return (
    <div className="text-sm text-column-gray-500 mt-1 min-w-[800px]">
      {labelText && (
        <div className="mb-2 min-w-[800px]">
          <Label id={`${labelText.toLowerCase()}-label`}>{labelText}</Label>
        </div>
      )}
      <Listbox value={value} onChange={onChange} multiple>
        {({ open }) => (
          <div className="relative w-full">
            <Listbox.Button
              className={classNames(
                'flex items-center bg-white w-full rounded-md font-medium focus:outline-none text-sm',
                {
                  border: true,
                  'p-3': true,
                  'text-primary-500': false,
                  'border-column-gray-200 focus:border-column-primary-500 focus:shadow-outline-column-primary':
                    true,
                  'text-column-gray-300 cursor-default': false,
                  'bg-column-gray-25': false
                }
              )}
            >
              <span
                className={classNames('truncate text-sm', {
                  'text-column-gray-300': selectedOptions.length === 0
                })}
              >
                {getSelectLabelValue()}
              </span>
              <ChevronDownIcon aria-hidden="true" className="h-4 w-4 ml-auto" />
            </Listbox.Button>
            {open && (
              <Listbox.Options
                className={classNames(
                  'absolute max-h-64 overflow-auto w-full bg-white p-1.5 mt-2 rounded border border-column-gray-100 focus:outline-none z-50 shadow-column-3',
                  {
                    hidden: !open
                  }
                )}
                static
              >
                {options.length === 0 && (
                  <div className="text-center py-2 text-column-gray-300">
                    No options
                  </div>
                )}
                {options.map((option, index) => (
                  <div
                    key={`${option.value}-${index}`}
                    className="w-full min-w-[500px]"
                  >
                    <Listbox.Option value={option.value} className="w-full">
                      {({ active, selected }) => (
                        <ColumnSelectItem
                          option={option}
                          active={active}
                          selected={selected}
                          showCheckBox
                        />
                      )}
                    </Listbox.Option>
                  </div>
                ))}
              </Listbox.Options>
            )}
          </div>
        )}
      </Listbox>
    </div>
  );
}
